import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { countyForZip } from "../config/serviceArea.config";
import { submitLead, newEventId, readUtm } from "../lib/lead";
import type { Timeline, InvestmentBand } from "../lib/lead";
import { trackViewContent, trackLead, trackLeadFormStarted, getCookie } from "../lib/pixel";
import BookingStep from "../components/BookingStep";

/**
 * /quiz — captura corta editorial (rediseño 2026-09-28). Reemplaza el wizard largo:
 * proyecto -> timeline -> inversión -> contacto -> calendario. Sin emojis, estilo
 * olive/bone/amber + Cormorant. Dispara Lead + push a GHL (interiors/interiors-lp).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PROJECTS = [
  { id: "whole_home", label: "Whole home", sub: "Every room, one vision" },
  { id: "kitchen", label: "Kitchen", sub: "The heart of the home" },
  { id: "living", label: "Living & entertaining", sub: "Living, dining, great room" },
  { id: "primary", label: "Primary suite", sub: "Bedroom and bath retreat" },
  { id: "addition", label: "Addition or new build", sub: "Adding, or building from scratch" },
];
const TIMELINES: { id: string; tl: Timeline; label: string; sub: string }[] = [
  { id: "ready", tl: "ready", label: "Ready now", sub: "Let's get moving" },
  { id: "1-3m", tl: "1-3m", label: "In 1 to 3 months", sub: "Planning ahead" },
  { id: "3-6m", tl: "3-6m", label: "In 3 to 6 months", sub: "On the horizon" },
  { id: "exploring", tl: "exploring", label: "Just exploring", sub: "Gathering ideas" },
];
const TIERS: { id: string; label: string; sub: string; band: InvestmentBand; range: string; value: number }[] = [
  { id: "t1", label: "Signature spaces", sub: "$40-75K, a refined refresh", band: "A", range: "$40-75K", value: 57500 },
  { id: "t2", label: "A full transformation", sub: "$75-150K", band: "A", range: "$75-150K", value: 112500 },
  { id: "t3", label: "A full-home reimagining", sub: "$150-300K", band: "B", range: "$150-300K", value: 225000 },
  { id: "t4", label: "Estate-level", sub: "$300-500K", band: "C", range: "$300-500K", value: 400000 },
  { id: "t5", label: "No ceiling", sub: "$500K and up", band: "C", range: "$500K+", value: 600000 },
  { id: "unsure", label: "Guide me to the right level", sub: "We'll help you scope it", band: null, range: "", value: 150000 },
];

type Step = "project" | "timeline" | "tier" | "contact" | "booked";

export default function Consult() {
  const [step, setStep] = useState<Step>("project");
  const [project, setProject] = useState("");
  const [timeline, setTimeline] = useState<{ tl: Timeline; id: string } | null>(null);
  const [tier, setTier] = useState("");
  const [c, setC] = useState({ first_name: "", phone: "", email: "", zip: "" });
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);
  const eventId = useRef(newEventId());
  useEffect(() => trackViewContent("consult"), []);

  const start = () => { if (!started.current) { started.current = true; trackLeadFormStarted("consultation"); } };
  const idx = { project: 0, timeline: 1, tier: 2, contact: 3, booked: 4 }[step];

  const pickProject = (id: string) => { start(); setProject(id); setTimeout(() => setStep("timeline"), 170); };
  const pickTimeline = (t: { tl: Timeline; id: string }) => { setTimeline(t); setTimeout(() => setStep("tier"), 170); };
  const pickTier = (id: string) => { setTier(id); setTimeout(() => setStep("contact"), 170); };
  const goBack = () => {
    if (step === "timeline") setStep("project");
    else if (step === "tier") setStep("timeline");
    else if (step === "contact") setStep("tier");
  };

  async function submitContact() {
    const e: Record<string, string> = {};
    if (!c.first_name.trim()) e.first_name = "Your name?";
    if (!/\d{10,}/.test(c.phone.replace(/\D/g, ""))) e.phone = "A valid phone, please.";
    if (!EMAIL_RE.test(c.email)) e.email = "A valid email, please.";
    if (!countyForZip(c.zip)) e.zip = "We serve Pinellas, Hillsborough and Pasco.";
    if (!consent) e.consent = "Please agree to be contacted.";
    setErrors(e);
    if (Object.keys(e).length) return;
    const t = TIERS.find((x) => x.id === tier)!;
    setSubmitting(true);
    const res = await submitLead({
      lead_source: "consultation",
      first_name: c.first_name,
      phone: c.phone,
      email: c.email,
      address: "",
      zip: c.zip,
      bathroom_scope: null,
      interiors_scope: { project, investment_tier: tier },
      investment_band: t.band,
      investment_tier: tier,
      estimator_range: t.range || null,
      timeline: timeline?.tl ?? null,
      consent: true,
      utm: readUtm(),
      event_id: eventId.current,
      fbp: getCookie("_fbp"),
      fbc: getCookie("_fbc"),
      page_url: typeof window !== "undefined" ? window.location.href : undefined,
    });
    setSubmitting(false);
    if (res.ok) { trackLead(eventId.current, t.value); setStep("booked"); }
    else setErrors({ form: "Something went wrong. Please try again." });
  }

  const Card = ({ label, sub, onClick, selected }: { label: string; sub: string; onClick: () => void; selected?: boolean }) => (
    <button onClick={onClick} className={`group flex w-full items-center justify-between gap-4 border-b bg-transparent py-5 text-left transition ${selected ? "border-clay" : "border-stone/25 hover:border-clay"}`}>
      <span>
        <span className="block font-serif text-2xl leading-tight text-ink">{label}</span>
        <span className="mt-0.5 block font-sans text-[11px] uppercase tracking-[0.16em] text-stone">{sub}</span>
      </span>
      <span className="font-sans text-lg text-stone transition group-hover:translate-x-1 group-hover:text-clay">&rarr;</span>
    </button>
  );
  const inp = "w-full rounded-md border border-stone/30 bg-white/70 px-4 py-3 font-sans text-ink outline-none transition focus:border-clay";

  return (
    <main className="min-h-[100dvh] bg-bone px-6 py-7 text-ink md:px-10">
      <div className="mx-auto max-w-lg">
        <div className="flex items-center justify-between">
          {step !== "project" && step !== "booked" ? (
            <button onClick={goBack} className="font-sans text-xs uppercase tracking-[0.18em] text-stone hover:text-ink">&larr; Back</button>
          ) : (
            <Link to="/" className="font-serif text-xl text-ink hover:text-clay">A Couple &amp; A Dream</Link>
          )}
          {idx < 4 && (
            <div className="flex gap-1.5">{[0, 1, 2, 3].map((i) => <span key={i} className={`h-[3px] rounded-full transition-all ${i <= idx ? "w-7 bg-clay" : "w-3.5 bg-stone/30"}`} />)}</div>
          )}
        </div>

        {step === "project" && (
          <div className="mt-12">
            <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-clay">A design consultation with Ana Clara and Javier</p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.05] md:text-5xl">What are we designing?</h1>
            <div className="mt-8">{PROJECTS.map((p) => <Card key={p.id} label={p.label} sub={p.sub} onClick={() => pickProject(p.id)} selected={project === p.id} />)}</div>
          </div>
        )}

        {step === "timeline" && (
          <div className="mt-12">
            <h1 className="font-serif text-4xl leading-[1.05] md:text-5xl">When would you like to begin?</h1>
            <p className="mt-3 font-sans text-stone">There is no wrong answer. It just helps us plan.</p>
            <div className="mt-8">{TIMELINES.map((t) => <Card key={t.id} label={t.label} sub={t.sub} onClick={() => pickTimeline(t)} selected={timeline?.id === t.id} />)}</div>
          </div>
        )}

        {step === "tier" && (
          <div className="mt-12">
            <h1 className="font-serif text-4xl leading-[1.05] md:text-5xl">Where does this project sit for you?</h1>
            <p className="mt-3 font-sans text-stone">So we match you to the right level of the studio.</p>
            <div className="mt-8">{TIERS.map((t) => <Card key={t.id} label={t.label} sub={t.sub} onClick={() => pickTier(t.id)} selected={tier === t.id} />)}</div>
          </div>
        )}

        {step === "contact" && (
          <div className="mt-12">
            <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-clay">One last thing</p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.05] md:text-5xl">Where should Ana Clara and Javier reach you?</h1>
            <p className="mt-3 font-sans text-stone">Your consultation is with the founders themselves, the couple who designs and builds it.</p>
            <div className="mt-8 space-y-3">
              <input className={inp} placeholder="First name" autoComplete="given-name" value={c.first_name} onChange={(e) => setC({ ...c, first_name: e.target.value })} />
              {errors.first_name && <p className="text-xs text-clay">{errors.first_name}</p>}
              <input className={inp} placeholder="Phone" type="tel" autoComplete="tel" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} />
              {errors.phone && <p className="text-xs text-clay">{errors.phone}</p>}
              <input className={inp} placeholder="Email" type="email" autoComplete="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} />
              {errors.email && <p className="text-xs text-clay">{errors.email}</p>}
              <input className={inp} placeholder="ZIP code" inputMode="numeric" autoComplete="postal-code" value={c.zip} onChange={(e) => setC({ ...c, zip: e.target.value })} />
              {errors.zip && <p className="text-xs text-clay">{errors.zip}</p>}
              <label className="flex items-start gap-2 pt-1 font-sans text-xs leading-relaxed text-stone">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-clay" />
                <span>I agree to be contacted by A Couple &amp; A Dream about my project.</span>
              </label>
              {errors.consent && <p className="text-xs text-clay">{errors.consent}</p>}
              {errors.form && <p className="text-xs text-clay">{errors.form}</p>}
              <button onClick={submitContact} disabled={submitting} className="w-full rounded-full bg-clay py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-ink transition hover:bg-ink hover:text-bone disabled:opacity-60">
                {submitting ? "One moment..." : "See my time"}
              </button>
            </div>
          </div>
        )}

        {step === "booked" && (
          <div className="mt-10">
            <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-clay">Pick a time</p>
            <h1 className="mt-3 font-serif text-4xl leading-[1.05] md:text-5xl">Book your consultation</h1>
            <p className="mt-3 font-sans text-stone">Ana Clara and Javier walk your home, listen to how you live, and turn it into a real, buildable plan.</p>
            <div className="mt-6"><BookingStep value={TIERS.find((t) => t.id === tier)?.value ?? 0} hideHeader /></div>
          </div>
        )}
      </div>
    </main>
  );
}
